import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import LayoutMain from "components/Layout/LayoutMain";
import { QuoteDetail } from "features/quotation/types";
import sortQuoteDetailWheelsForDisplay from "features/quotation/utils/sortQuoteDetailWheelsForDisplay";
import CardCarModelPdf from "features/car_model/components/CardCarModelPdf";
import TableWheelTireEstResultPdf from "features/car_model/components/TableWheelTireEstResultPdf";
import ServiceCostSummary, {
  formatCostValueForDetail,
} from "features/mst_proc_fee/components/ServiceCostSummary";
import { CostSettingInfo } from "features/mst_proc_fee/types/proc-fee";
import { IInfoCell } from "features/mst_store_tire_wheel_filter/types/wheel";
import useWheelTireEstResultPdf from "features/car_model/hook/useWheelTireEstResultPdf";
import usePrintPdfShortcut from "features/car_model/hook/usePrintPdfShortcut";
import { formatCurrency, isPriceLabel } from "utils";
import { getTirePunctureDisplay } from "features/car_model/utils";
import PreviewWheelTirePdfEst from "features/car_model/components/PreviewWheelTirePdfEst";
import QRCode from "qrcode";
import ImageComponent from "components/image/ImageComponent";

const DISABLED_TEXT = "なし" as const;
const ENABLED_TEXT = "する" as const;
const TAKE_OUT_DISABLED_TEXT = "しない" as const;

const styles = {
  headerInfo: { fontSize: { xs: 16, md: 18, lg: 20 }, fontWeight: "bold" },
  label: { minWidth: 60 },
  value: { flex: 1 },
};

const CSS_VARIABLES = {
  GREEN: "var(--green-600)",
  OFFWHITE: "var(--offwhite-500)",
} as const;

// Helper functions
const getBackgroundColor = (isActive: boolean) =>
  isActive ? CSS_VARIABLES.GREEN : CSS_VARIABLES.OFFWHITE;

const FIELDS_REQUIRING_DETAIL_FORMAT: Array<keyof CostSettingInfo> = [
  "waste_tire_cost",
  "rubber_stem_valve_cost",
  "nitrogen_inflation_cost",
  "hub_rust_prevention_cost",
];

/**
 * Helper function to build CostSettingInfo from quotation data.
 * @param {QuoteDetail} quotation - The quotation object containing cost information.
 * @returns {CostSettingInfo} The formatted cost setting information.
 */
const buildCostSettingInfo = (quotation: QuoteDetail): CostSettingInfo => ({
  replac_labor_cost: formatCurrency(quotation.replac_labor_cost || 0),
  waste_tire_cost_flag: Boolean(quotation.waste_tire_cost_flag === 1),
  waste_tire_cost: formatCurrency(quotation.waste_tire_cost || 0),
  rubber_stem_valve_flag: Boolean(quotation.rubber_stem_valve_flag === 1),
  rubber_stem_valve_cost: formatCurrency(quotation.rubber_stem_valve_cost || 0),
  nitrogen_inflation_flag: Boolean(quotation.nitrogen_inflation_flag === 1),
  nitrogen_inflation_cost:
    quotation.nitrogen_inflation_flag === 1
      ? formatCurrency(quotation.nitrogen_inflation_cost || 0)
      : "-",
  hub_rust_prevention_flag: Boolean(quotation.hub_rust_prevention_flag === 1),
  hub_rust_prevention_cost:
    quotation.hub_rust_prevention_flag === 1
      ? formatCurrency(quotation.hub_rust_prevention_cost || 0)
      : "-",
  tire_puncture_flag: quotation.tire_puncture_flag,
  cashback_flag: false,
  cashback_period: 1,
  rounding: 3,
  take_out: quotation.take_out_flag === 0 ? ENABLED_TEXT : TAKE_OUT_DISABLED_TEXT,
  nut: quotation.nut_item_cd
    ? `${quotation.nut_option_name}\u00A0\u00A0${formatCurrency(quotation.nut_sale_price || 0)}円`
    : DISABLED_TEXT,
  lock_nut: quotation.lock_nut_item_cd
    ? `${quotation.locknut_option_name}\u00A0\u00A0${formatCurrency(quotation.locknut_sale_price || 0)}円`
    : DISABLED_TEXT,
  hub_ring: quotation.hub_ring_item_cd
    ? `${quotation.hubring_option_name}\u00A0\u00A0${formatCurrency(quotation.hubring_sale_price || 0)}円`
    : DISABLED_TEXT,
  wheel_coating: quotation.wcoating_item_cd
    ? `${quotation.wcoating_option_name}\u00A0\u00A0${formatCurrency(quotation.wcoating_sale_price || 0)}円`
    : DISABLED_TEXT,
  take_out_flag: quotation.take_out_flag === 1,
  nut_flag: Boolean(quotation.nut_item_cd),
  lock_nut_flag: Boolean(quotation.lock_nut_item_cd),
  hub_ring_flag: Boolean(quotation.hub_ring_item_cd),
  wheel_coating_flag: Boolean(quotation.wcoating_item_cd),
  tire_puncture: getTirePunctureDisplay(quotation.tire_puncture_flag),
});

/**
 * EstResult component renders the result page for an estimation result with PDF preview.
 */
export default function WheelTireEstResultPdf() {
  const { quoteId } = useParams<{ quoteId: string }>();

  const { quotation } = useWheelTireEstResultPdf({
    quote_id: quoteId ? Number(quoteId) : undefined,
  });

  const [qrCodeData, setQrCodeData] = useState<string | null>(null);

  const quoteInfo: CostSettingInfo = useMemo(
    () =>
      quotation
        ? buildCostSettingInfo(quotation)
        : {
          replac_labor_cost: "-",
          waste_tire_cost_flag: false,
          waste_tire_cost: "-",
          rubber_stem_valve_flag: false,
          rubber_stem_valve_cost: "-",
          nitrogen_inflation_flag: false,
          nitrogen_inflation_cost: "-",
          hub_rust_prevention_flag: false,
          hub_rust_prevention_cost: "-",
          tire_puncture_flag: 0,
          cashback_flag: false,
          cashback_period: 1,
          rounding: 3,
          take_out: TAKE_OUT_DISABLED_TEXT,
          nut: "-",
          lock_nut: "-",
          hub_ring: "-",
          wheel_coating: "-",
          take_out_flag: false,
          nut_flag: false,
          lock_nut_flag: false,
          hub_ring_flag: false,
          wheel_coating_flag: false,
          tire_puncture: "-",
        },
    [quotation],
  );

  // Helper function to create cost item configuration
  const createCostItem = useCallback(
    (
      label: string,
      fieldName: keyof CostSettingInfo,
      fieldNameLabel?: keyof CostSettingInfo,
      customConfig?: Partial<IInfoCell>,
    ): IInfoCell => {
      let displayValue = quoteInfo[fieldName] as string | number;

      if (
        fieldNameLabel &&
        FIELDS_REQUIRING_DETAIL_FORMAT.includes(fieldName) &&
        fieldNameLabel in quoteInfo
      ) {
        displayValue = formatCostValueForDetail(quoteInfo[fieldNameLabel], displayValue);
      }

      const valueStr = String(displayValue ?? "");
      // Same layout as TableEstDiscount: Box flex with ¥ and value separate for spacing
      const valueWithYen =
        isPriceLabel(valueStr) && valueStr !== ""
          ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              <Box component="span">¥</Box>
              <Box component="p" sx={{ textAlign: "center", width: "100%", my: 0 }}>
                {valueStr}
              </Box>
            </Box>
          )
          : valueStr;

      const baseConfig: IInfoCell = {
        sxBox: { width: "142px" },
        label,
        labelType: "text",
        sxLabel: {
          backgroundColor: fieldNameLabel
            ? getBackgroundColor(
              fieldNameLabel === "tire_puncture_flag"
                ? false
                : Boolean(quoteInfo[fieldNameLabel]),
            )
            : "var(--green-600)",
          whiteSpace: "nowrap",
        },
        value: valueWithYen,
        valueType: "text",
        sxValue: { textAlign: "center" },
        fieldName: fieldName as string,
        ...(fieldNameLabel && { fieldNameLabel: fieldNameLabel as string }),
      };

      return { ...baseConfig, ...customConfig };
    },
    [quoteInfo],
  );

  const costItems: IInfoCell[] = useMemo(
    () => [
      createCostItem("交換工賃", "replac_labor_cost"),
      createCostItem("処分", "waste_tire_cost", "waste_tire_cost_flag"),
      createCostItem("窒素", "nitrogen_inflation_cost", "nitrogen_inflation_flag"),
      createCostItem("ハブ防錆", "hub_rust_prevention_cost", "hub_rust_prevention_flag"),
      createCostItem("パンク補償", "tire_puncture", "tire_puncture_flag"),
    ],
    [quoteInfo, createCostItem],
  );

  const optionsPrice: number = useMemo(() => {
    if (!quotation) return 0;
    return (
      (quotation.nut_sale_price || 0) +
      (quotation.locknut_sale_price || 0) +
      (quotation.hubring_sale_price || 0) +
      (quotation.wcoating_sale_price || 0)
    );
  }, [quotation]);

  const sortedQuoteWheels = useMemo(
    () => sortQuoteDetailWheelsForDisplay(quotation?.tbl_quote_detail_wheel),
    [quotation?.tbl_quote_detail_wheel],
  );

  // Generate QR code from quotation
  useEffect(() => {
    if (quotation) {
      QRCode.toDataURL(
        `${window.location.origin}/quotation-qr/${quotation.quote_id}?qr_read=yes&wheel=yes`,
      )
        .then((url) => {
          setQrCodeData(url);
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error("Error generating QR code:", error);
        });
    } else {
      setQrCodeData(null);
    }
  }, [quotation]);

  // Handle Ctrl + P to print PDF
  const pdfDocument = useCallback(() => {
    if (!quotation || !qrCodeData) {
      return null;
    }
    return <PreviewWheelTirePdfEst quotationDetail={quotation} qrCodeData={qrCodeData} optionsPrice={optionsPrice} />;
  }, [quotation, qrCodeData, optionsPrice]);

  usePrintPdfShortcut({
    pdfDocument,
    canPrint: () => {
      if (!quotation || !qrCodeData) return false;
      return Boolean(
        quotation.tbl_quote_detail_wheel && quotation.tbl_quote_detail_wheel.length > 0,
      );
    },
  });

  return (
    <LayoutMain
      sx={{
        flexDirection: "column",
        gap: 4,
        paddingX: 0,
        paddingY: 0,
        alignItems: "center",
        overflow: "hidden",
        minWidth: "1690px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: { sm: "50px", xl: "100px" },
          justifyContent: "space-between",
          width: "100%",
          maxWidth: "1690px",
          minWidth: "1690px",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Box display="flex">
            <Box sx={{ ...styles.headerInfo, ...styles.label }}>店舗名</Box>
            <Box sx={{ ...styles.headerInfo, ...styles.value }}>：{quotation?.store_name}</Box>
          </Box>
          <Box display="flex">
            <Box sx={{ ...styles.headerInfo, ...styles.label }}>住所</Box>
            <Box sx={{ ...styles.headerInfo, ...styles.value }}>：{quotation?.address}</Box>
          </Box>
          <Box display="flex">
            <Box sx={{ ...styles.headerInfo, ...styles.label }}>電話</Box>
            <Box sx={{ ...styles.headerInfo, ...styles.value }}>
              ：{quotation?.tel_no} &nbsp;&nbsp;&nbsp; 見積担当：{quotation?.quote_by}
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <ImageComponent
              sx={{ width: "80px", border: "unset" }}
              src="/img/home_qr_code.png"
              alt="home_qr_code"
              objectFit="contain"
            />
            <Typography sx={{ fontSize: { xs: 12, md: 13, lg: 14 }, whiteSpace: "nowrap" }}>
              当社グループは個人情報保護法に準拠し、適切な個人情報の取り扱いをしております。
              <br />
              プライバシーポリシーは当社HPに記載及び店頭に掲示しております。
            </Typography>
          </Box>
          <Typography sx={{ fontSize: { xs: 16, md: 18, lg: 20 }, fontWeight: "medium" }}>
            金額は全て税込となります
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
          alignItems: "flex-start",
          maxWidth: "1690px",
          minWidth: "1690px",
          borderBottom: "1px solid var(--gray-250)",
          pb: 2,
        }}
      >
        {quotation && <CardCarModelPdf quotation={quotation} showDates />}
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          width: "100%",
          gap: 4,
          boxSizing: "border-box",
          maxWidth: "1690px",
        }}
      >
        <Box sx={{ width: { xs: "100%", lg: "auto" }, overflowX: "auto" }}>
          <ServiceCostSummary
            type="detail"
            isBarCode
            costSettingInfo={quoteInfo}
            costItems={costItems}
            barcodeCodes={
              quotation
                ? {
                  nut_jan_cd: undefined,
                  locknut_jan_cd: quotation.locknut_jan_cd || undefined,
                  hubring_jan_cd: quotation.hubring_jan_cd || undefined,
                  wcoating_jan_cd: quotation.wcoating_jan_cd || undefined,
                }
                : undefined
            }
            sxBox={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "end",
              minWidth: "1370px",
            }}
          />
        </Box>
        {quotation && sortedQuoteWheels.length > 0
          ? sortedQuoteWheels.map((wheel) => (
            <Box
              key={`wheel-${wheel.quote_id}-${wheel.wheel_item_cd}-${wheel.pairing_number}`}
              sx={{
                display: "flex",
                maxWidth: "1690px",
                width: "100%",
                overflowX: "auto",
              }}
            >
              <TableWheelTireEstResultPdf
                optionsPrice={optionsPrice}
                wheelData={wheel}
                tireData={wheel.tbl_quote_detail_tire}
              />
            </Box>
          ))
          : null}

      </Box>

    </LayoutMain>
  );
}
